import React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components'
import { FormControlLabel, Radio } from '@mui/material'

import breakpoints from '../../../constants/breakpoints'
import theme from '../../../theme'
import Icon from '../../Icon'

interface RadioButtonType {
  onClick: (input: string) => void
  active: boolean
  to: string
}

interface Props extends RadioButtonType {
  iconName?: string
  value: string
}

const breakpointSm = `@media (min-width: ${breakpoints.sm})`
const breakpointMd = `@media (min-width: ${breakpoints.md})`

const defaultStyles = {
  backgroundColor: 'white',
  border: 'solid',
  borderColor: theme.lightGrey,
  borderRadius: 0.5,
  borderWidth: 1,
  alignItems: 'center',
  fontFamily: theme.primaryFont,
  display: 'flex',
  flexDirection: 'row',
  padding: '15px',
  color: theme.blue,
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  height: '100%',
  margin: '0',
  '&>span': {
    fontSize: '1rem',
    lineHeight: '1.2',
    fontFamily: theme.primaryFont,
    display: 'flex',
    [breakpointMd]: {
      fontSize: '1.5rem',
    },
  },
  '& svg': {
    fill: theme.blue,
    height: '15px',
    marginRight: '10px',
    transition: 'all 0.3s ease',
    verticalAlign: 'middle',
    width: '15px',
    display: 'inline-block',
    [breakpointSm]: {
      height: '20px',
      width: '20px',
    },
  },
  '&:hover': {
    boxShadow: '0 2px 10px 0 rgba(30, 30, 30, 0.4)',
    backgroundColor: theme.blue,
    borderColor: theme.blue,
    color: 'white',
    '& svg': {
      fill: 'white',
    },
  },
}

const activeStyles = {
  backgroundColor: theme.blue,
  border: `1px solid ${theme.blue}`,
  borderColor: theme.lightGrey,
  borderRadius: 0.5,
  alignItems: 'center',
  fontFamily: theme.primaryFont,
  display: 'flex',
  flexDirection: 'row',
  padding: '15px',
  color: 'white',
  justifyContent: 'center',
  transition: 'all 0.3s ease',
  height: '100%',
  margin: '0',
  '&>span': {
    fontSize: '1rem',
    lineHeight: '1.2',
    fontFamily: theme.primaryFont,
    display: 'flex',
    [breakpointMd]: {
      fontSize: '1.5rem',
    },
  },
  '& svg': {
    fill: 'white',
    height: '15px',
    marginRight: '10px',
    transition: 'all 0.3s ease',
    verticalAlign: 'middle',
    width: '15px',
    display: 'inline-block',
    [breakpointSm]: {
      height: '20px',
      width: '20px',
    },
  },
  '&:hover': {
    boxShadow: '0 2px 10px 0 rgba(30, 30, 30, 0.4)',
    backgroundColor: theme.blue,
    borderColor: theme.blue,
    color: 'white',
    '& svg': {
      fill: 'white',
    },
  },
}

const RadioButton: React.FC<Props> = ({ onClick, value, to, iconName, active }) => {
  const handleTab = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (e.key === 'Enter') onClick(value)
  }

  return (
    <RadioButtonContainer>
      <FormControlLabel
        value={value}
        control={<Radio color="primary" />}
        sx={active ? [activeStyles] : [defaultStyles]}
        label={
          iconName ? (
            <>
              <Icon icon={iconName} />
              {value}
              <Link className="overlay-link" to={to} onKeyDown={handleTab} aria-hidden="true">
                <span className="sr-only">{value}</span>
              </Link>
            </>
          ) : (
            <>
              {value}
              <Link className="overlay-link" to={to} onKeyDown={handleTab} aria-hidden="true">
                <span className="sr-only">{value}</span>
              </Link>
            </>
          )
        }
        onClick={() => onClick(value)}
      />
    </RadioButtonContainer>
  )
}

const RadioButtonContainer = styled.div`
  margin: 10px 0 0;
  flex: 0 0 auto;
  position: relative;

  span:first-child {
    visibility: hidden;
    width: 0;
    height: 0;
    padding: 0;

    :active {
      background-color: ${theme.blue};
      border-color: ${theme.blue};
      color: 'white';

      svg: {
        fill: 'white';
      }
    }
  }

  @media (min-width: ${breakpoints.md}) {
    margin: 30px 10px 0;
    width: 30%;
  }

  & a {
    text-decoration: none;
  }
`

export default RadioButton
